import { FC, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/helper/api";
import {
  Controls,
  Edge,
  MarkerType,
  MiniMap,
  ReactFlow,
  ReactFlowProvider,
} from "@xyflow/react";
import {
  CustomNode,
  transformResponseToConnectionState,
  transfromResponseToActionState,
} from "@/components/actionChain/actionChainCreateAndEdit";
import { useErrorModal } from "@/components/forms/errorHandling/useErrorModal";
import { useTranslation } from "react-i18next";
import ActionChainCustomNode from "@/components/actionChain/actionChainCustomNode";
import ActionChainCustomConnection from "@/components/actionChain/actionChainCustomConnection";
import styles from "@/components/actionChain/actionChains.module.css";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import { Tooltip } from "@mui/material";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import { usePageHeading } from "@/helper/customHooks/usePageHeading";

const ActionChainProgress: FC = () => {
  const { t } = useTranslation();
  const [action, setAction] = useState<CustomNode[]>([]);
  const [connection, setConnection] = useState<Edge[]>([]);
  const [templateName, setTemplateName] = useState<string>("");
  const [subjectName, setSubjectName] = useState<string>("");
  const heading = [templateName, subjectName].filter(Boolean).join(" - ");
  usePageHeading(heading || t("actionchains"));
  const { handleError, errorModal } = useErrorModal();

  const { actionChainTemplateId, actionChainProgressId } = useParams() as {
    actionChainTemplateId: string;
    actionChainProgressId: string;
  };
  const navigate = useNavigate();

  const nodesWithDeleteHandlerWidth = "300px";
  const nodesWithDeleteHandlerHeight = "95px";
  const companyProgressNodeWidth = "150px";
  const companyProgressNodeHeight = "";

  useEffect(() => {
    getActionChainTemplateItemsById(
      actionChainTemplateId,
      setAction,
      setConnection,
      setTemplateName,
      handleError
    );
    getActionChainProgressItemsById(
      actionChainProgressId,
      setAction,
      setConnection,
      setSubjectName,
      handleError
    );
  }, [actionChainTemplateId, actionChainProgressId]);

  const customNodes = action
    .filter((node) => node.actionChainItem?.metadata.submitMethod !== "delete")
    .map((node) => ({
      ...node,
      type: "companyProgress",
      data: {
        ...node.data,
      },
    }));

  const customConnection = connection.map((con) => ({
    ...con,
    type: "companyProgress",
    markerEnd: {
      type: MarkerType.ArrowClosed,
    },

    data: {
      ...con.data,
    },
  }));
  const nodeTypes = useMemo(
    () => ({
      withDeleteHandler: (props: any) => (
        <ActionChainCustomNode
          {...props}
          nodeWidth={nodesWithDeleteHandlerWidth}
          nodeHeight={nodesWithDeleteHandlerHeight}
        />
      ),
      companyProgress: (props: any) => (
        <ActionChainCustomNode
          {...props}
          nodeWidth={companyProgressNodeWidth}
          nodeHeight={companyProgressNodeHeight}
          fontSize="12px"
        />
      ),
    }),
    []
  );

  const edgeTypes = useMemo(
    () => ({
      withDeleteHandler: (props: any) => (
        <ActionChainCustomConnection {...props} style={{ color: "red" }} />
      ),
      companyProgress: (props: any) => (
        <ActionChainCustomConnection {...props} style={{ color: "red" }} />
      ),
    }),
    []
  );

  const handleNavigateBack = () => {
    navigate(-1);
  };
  return (
    <>
      <div className={styles.heading_wrapper}>
        <Tooltip placement="right" title={t("back")}>
          <GenericButton
            variant="icon"
            color="iconBase"
            className={styles.backButton}
            onClick={handleNavigateBack}
          >
            <ArrowBackIcon />
          </GenericButton>
        </Tooltip>
      </div>
      <ReactFlowProvider>
        <ReactFlow
          nodes={customNodes}
          nodeTypes={nodeTypes}
          edges={customConnection}
          edgeTypes={edgeTypes}
          snapToGrid={true}
          snapGrid={[15, 15]}
          fitView
        >
          <Controls />
          <MiniMap />
        </ReactFlow>
      </ReactFlowProvider>
      {errorModal}
    </>
  );
};

export default ActionChainProgress;

function getActionChainTemplateItemsById(
  actionChainTemplateId: string,
  setAction: any,
  setConnection: any,
  setTemplateName: any,
  handleError: any
) {
  const additionnal: any = {};
  additionnal["action_chain_template"] = actionChainTemplateId;
  api.genericApiRequest({
    method: "get",
    entity: "actionChainItemTemplate",
    parametersToRender: { additionalUrlParameters: additionnal },
    successHandler: (res: any) => {
      const data = res.data.results;
      if (!res.data.results || res.data.results.length === 0) {
        return;
      }

      const tempActionStateData = transfromResponseToActionState(data);
      const tempConnectionStateData = transformResponseToConnectionState(data);

      setAction(tempActionStateData);
      setConnection(tempConnectionStateData);
      setTemplateName(data?.[0].action_chain_template?.name);
    },
    failHandler: (error: any) => {
      handleError(error);
    },
  });
}
function getActionChainProgressItemsById(
  actionChainProgressId: string,
  setAction: any,
  setConnection: any,
  setSubjectName: any,

  handleError: any
) {
  const additionnal: any = {};
  additionnal["action_chain_progress"] = actionChainProgressId;
  api.genericApiRequest({
    method: "get",
    entity: "actionChainItemProgress",
    parametersToRender: { additionalUrlParameters: additionnal },
    successHandler: (res: any) => {
      const progressData = res.data.results;
      setSubjectName(progressData?.[0].action_chain_progress?.subject?.name);

      setAction((prevActions: CustomNode[]) =>
        mergeProgressIntoActions(prevActions, progressData)
      );
      setConnection((prevConnections: Edge[]) =>
        mergeProgressIntoConnections(prevConnections, progressData)
      );
    },
    failHandler: (error: any) => {
      handleError(error);
    },
  });
}

function mergeProgressIntoActions(
  actions: CustomNode[],
  progressData: any[]
): CustomNode[] {
  const progressMap = new Map(
    progressData.map((item) => [
      item.action_chain_item_template.id,
      item.status,
    ])
  );

  return actions.map((action) => {
    const progressStatus = progressMap.get(action.id);
    if (progressStatus) {
      return {
        ...action,
        data: {
          ...action.data,
          status: progressStatus,
        },
      };
    }
    return action;
  });
}
function mergeProgressIntoConnections(
  connections: Edge[],
  progressData: any[]
): Edge[] {
  const progressMap = new Map(
    progressData.map((item) => [
      item.action_chain_item_template.id,
      item.status,
    ])
  );

  return connections.map((connection: any) => {
    const targetId = connection?.data?.targetData?.id;

    if (targetId && progressMap.has(targetId)) {
      return {
        ...connection,
        data: {
          ...connection.data,
          targetData: {
            ...connection.data.targetData,
            status: progressMap.get(targetId),
          },
        },
      };
    }

    return connection;
  });
}
